import endpoints from '@app/src/api/endpoints'
import type { FetchBaseParameters } from '@app/src/api/fetchHooks'
import { FetchKey, useFetchCount, useFetchResource } from '@app/src/api/fetchHooks'
import EmptyState from '@app/src/components/EmptyState'
import { EmptyStateVariant } from '@app/src/components/EmptyState/EmptyState'
import HasWriteAccess from '@app/src/components/HasWriteAccess'
import { useStringifyQueryFilters } from '@app/src/hooks/queryState'
import useSignalRListener from '@app/src/hooks/signalRListener'
import type { DistributionGraphBar } from '@app/src/pages/Dashboards/DistributionGraph'
import DistributionGraph from '@app/src/pages/Dashboards/DistributionGraph'
import ScreeningInProgress from '@app/src/pages/Dashboards/ScreeningInProgress'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { ResponseScrapingStatus } from '@app/src/types/resourceExplorer'
import { br } from '@app/src/utils/translationMarkup'
import paths from '@app/src/wf-constants/paths'
import SignalCellularAltOutlinedIcon from '@mui/icons-material/SignalCellularAltOutlined'
import WifiOffIcon from '@mui/icons-material/WifiOff'
import { Box, Skeleton, Stack } from '@mui/material'
import { useTheme } from '@mui/styles'
import { useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { useInView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'
import { generatePath } from 'react-router'
import { useDebouncedCallback } from 'use-debounce'
import { ViewTypeName } from '../ResourceCollection/Collections/ManageRequests/ManageRequestsScene'
import RemainingProviderScreeningsButton from './RemainingProviderScreeningsButton'
import StatisticsCard from './StatisticsCard'

enum ScrapedAnswerType {
  DataFound = 'DataFound',
  NoDataFound = 'NoDataFound',
}

const SCRAPER_REFETCH_DEBOUNCE_MS = 2000
const SCRAPER_REFETCH_MAX_WAIT_MS = 5000

const toPercentage = (scrapedWithAnswersCount: number, total: number) => {
  return Math.round((scrapedWithAnswersCount / total) * 100)
}

export const SCRAPER_IS_RUNNING_QUERY_KEY: FetchBaseParameters<unknown>['key'] = [FetchKey.JobRunning, 'scraper']

const ScrapedAnswerByDataFound = () => {
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const { palette } = useTheme()
  const { stringifyQueryFilters } = useStringifyQueryFilters()
  const { inView, ref } = useInView({ triggerOnce: true })

  const {
    count: noDataCount,
    isLoading: scrapedWithAnswersLoading,
    isError: scrapedWithAnswersError,
  } = useFetchCount({
    key: FetchKey.ResponseScrapingCount,
    endpoint: endpoints.responsesScrapingCount,
    payload: [
      { name: 'status', filters: [{ operator: Operators.EqualTo, value: ResponseScrapingStatus.Completed }] },
      { name: 'percentageFound', filters: [{ operator: Operators.EqualTo, value: 0 }] },
    ],
    shouldShowErrorNotification: false,
  })

  const {
    count: dataFoundCount,
    isLoading: scrapedWithoutAnswersLoading,
    isError: scrapedWithoutAnswersError,
  } = useFetchCount({
    key: FetchKey.ResponseScrapingCount,
    endpoint: endpoints.responsesScrapingCount,
    payload: [
      { name: 'status', filters: [{ operator: Operators.EqualTo, value: ResponseScrapingStatus.Completed }] },
      { name: 'percentageFound', filters: [{ operator: Operators.NotEqualTo, value: 0 }] },
    ],
    shouldShowErrorNotification: false,
  })

  const {
    data: scraperIsRunning,
    isFetching: isFetchingScraperRunning,
    isError: scaperIsRunningIsError,
    refetch: refetchScraperIsRunning,
  } = useFetchResource<boolean>({
    endpoint: endpoints.scraperRunning,
    key: SCRAPER_IS_RUNNING_QUERY_KEY,
    shouldShowErrorNotification: false,
    options: {
      enabled: inView,
    },
  })

  const refetchScrapingProgress = useDebouncedCallback(
    () => {
      refetchScraperIsRunning()
      queryClient.invalidateQueries({ queryKey: [FetchKey.ResponseScrapingCount] })
    },
    SCRAPER_REFETCH_DEBOUNCE_MS,
    { maxWait: SCRAPER_REFETCH_MAX_WAIT_MS },
  )
  useSignalRListener('AiScrapingCompleted', refetchScrapingProgress)
  useSignalRListener('AiScrapingBatchCompleted', refetchScrapingProgress)

  const isError = (scrapedWithoutAnswersError || scrapedWithAnswersError || scaperIsRunningIsError) && !scraperIsRunning
  const loading =
    (scrapedWithAnswersLoading || scrapedWithoutAnswersLoading || isFetchingScraperRunning) && !scraperIsRunning

  const generateLink = (dataFound: boolean) =>
    stringifyQueryFilters({
      url: generatePath(paths.manageRequest, {
        view: ViewTypeName.ScreenedRequests,
      }),
      queryParams: {
        filters: [
          {
            name: 'status',
            value: 'Completed',
            operator: Operators.EqualTo,
          },
          {
            name: 'percentageFound',
            value: 0,
            operator: dataFound ? Operators.NotEqualTo : Operators.EqualTo,
          },
        ],
      },
    })

  const allDataCount = (dataFoundCount ?? 0) + (noDataCount ?? 0)
  const emptyState = !dataFoundCount && !noDataCount

  const bars: DistributionGraphBar[] = [
    {
      name: ScrapedAnswerType.DataFound,
      count: dataFoundCount ?? 0,
      color: palette.communication.dark,
      label: formatMessage({ id: 'dashboard.sourcing.scrapedAnswerByDataFound.dataFound' }),
      detailedLabel: formatMessage(
        { id: 'dashboard.sourcing.scrapedAnswerByDataFound.dataFoundWithPercentage' },
        { count: dataFoundCount, percentage: toPercentage(dataFoundCount ?? 0, allDataCount) },
      ),
      link: generateLink(true),
    },
    {
      name: ScrapedAnswerType.NoDataFound,
      count: noDataCount ?? 0,
      color: palette.communication.main,
      label: formatMessage({ id: 'dashboard.sourcing.scrapedAnswerByDataFound.noDataFound' }),
      detailedLabel: formatMessage(
        { id: 'dashboard.sourcing.scrapedAnswerByDataFound.noDataFoundWithPercentage' },
        { count: noDataCount, percentage: toPercentage(noDataCount ?? 0, allDataCount) },
      ),
      link: generateLink(false),
    },
  ]

  return (
    <Box ref={ref} height="100%">
      <StatisticsCard
        title={formatMessage({ id: 'dashboard.sourcing.scrapedAnswerByDataFound.title' })}
        helperText={formatMessage({ id: 'dashboard.sourcing.scrapedAnswerByDataFound.helperText' }, { br })}
        loading={{
          isLoading: loading,
          skeleton: (
            <Stack width="100%">
              <Skeleton width="100%" height={50} sx={{ my: 1 }} />

              <Stack direction="row" spacing={2}>
                <Skeleton width="10%" height={32} variant="rounded" />
                <Skeleton width="10%" height={32} variant="rounded" />
                <Skeleton width="10%" height={32} variant="rounded" />
              </Stack>
            </Stack>
          ),
        }}
      >
        <>
          {isError ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <EmptyState
                variant={EmptyStateVariant.Error}
                title={formatMessage({ id: 'dashboard.sourcing.scrapedAnswerByDataFound.error.title' })}
                description=""
                iconComponent={WifiOffIcon}
              />
            </Box>
          ) : scraperIsRunning ? (
            <ScreeningInProgress />
          ) : emptyState ? (
            <Stack>
              <Stack>
                <EmptyState
                  variant={EmptyStateVariant.Small}
                  title={formatMessage({ id: 'dashboard.sourcing.scrapedAnswerByDataFound.noData.title' })}
                  iconComponent={SignalCellularAltOutlinedIcon}
                />
                <Stack>
                  <HasWriteAccess>
                    <RemainingProviderScreeningsButton />
                  </HasWriteAccess>
                </Stack>
              </Stack>
            </Stack>
          ) : (
            <DistributionGraph
              bars={bars}
              title={formatMessage({ id: 'dashboard.sourcing.scrapedAnswerByDataFound.title' })}
              actionButton={<RemainingProviderScreeningsButton />}
            />
          )}
        </>
      </StatisticsCard>
    </Box>
  )
}

export default ScrapedAnswerByDataFound
